import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  Button,
  CircularProgress,
  styled,
  Chip,
  Modal,
  IconButton,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Stack,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AvatarGroup,
  Tooltip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PersonAdd as PersonAddIcon,
  AssignmentInd as AssignmentIndIcon,
  Groups as GroupsIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  Assignment as AssignmentIcon,
  PieChart as PieChartIcon,
  WorkHistory as WorkHistoryIcon,
  CalendarMonth as CalendarMonthIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { taskDataService } from '../../../services/taskDataService';

// Modal için styled component
const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflowY: 'auto',
  padding: theme.spacing(2, 0),
}));

const ModalContent = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '90%',
  maxWidth: 1200,
  maxHeight: '90vh',
  overflowY: 'auto',
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ModalSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
}));

// Görev durumuna göre renk döndür
const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success.main';
    case 'in_progress':
      return 'info.main';
    case 'pending':
    default:
      return 'warning.main';
  }
};

// Görev durumuna göre metin döndür
const getStatusText = (status: string) => {
  switch (status) {
    case 'completed':
      return 'Tamamlandı';
    case 'in_progress':
      return 'Devam Ediyor';
    case 'pending':
    default:
      return 'Beklemede';
  }
};

interface TeamDetailsModalProps {
  open: boolean;
  onClose: () => void;
  selectedTeam: any;
  companyId: string | null;
  personnel?: any[];
  onSaved?: () => void;
}

const TeamDetailsModal: React.FC<TeamDetailsModalProps> = ({
  open,
  onClose,
  selectedTeam,
  companyId,
  personnel = [],
  onSaved
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [teamTasks, setTeamTasks] = useState<any[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('Görevli');
  const [selectedDuty, setSelectedDuty] = useState<string>('');

  // Düzenlenebilir alanlar için state
  const [editableName, setEditableName] = useState('');
  const [editableDescription, setEditableDescription] = useState('');
  const [editableColor, setEditableColor] = useState('');

  // Üye tablosu için sayfalama
  const [membersPage, setMembersPage] = useState(0);
  const [membersRowsPerPage, setMembersRowsPerPage] = useState(5);

  // Görev tablosu için sayfalama
  const [tasksPage, setTasksPage] = useState(0);
  const [tasksRowsPerPage, setTasksRowsPerPage] = useState(5);

  // Görevleri modal açıldığında yükle
  useEffect(() => {
    if (open && selectedTeam) {
      setEditableName(selectedTeam.name || '');
      setEditableDescription(selectedTeam.description || '');
      setEditableColor(selectedTeam.color || '#1976D2');
      fetchTeamTasks();
    }
  }, [open, selectedTeam]);

  // Ekibe ait görevleri getir — SQL: ekibin projelerindeki görevler (taskDataService)
  const fetchTeamTasks = async () => {
    if (!companyId || !selectedTeam?.sqlId) { setTeamTasks([]); return; }
    
    setLoadingTasks(true);
    try {
      const [projectsRes, allTasks] = await Promise.all([
        taskDataService.listTeamProjects({ teamGroupId: selectedTeam.sqlId, pageSize: 200 }),
        taskDataService.listAllTeamProjectTasks(),
      ]);
      const projectIds = new Set((projectsRes.items || []).map(p => p.id));
      const rows = (allTasks || [])
        .filter(tk => projectIds.has(tk.teamProjectId))
        .map(tk => ({
          id: tk.id,
          name: tk.name,
          description: tk.description || '',
          status: tk.status,
          createdAt: tk.createdAt ? new Date(tk.createdAt).getTime() : undefined,
          dueDate: tk.dueDate ? new Date(tk.dueDate).getTime() : undefined,
          progress: tk.status === 'completed' ? 100 : 0,
        }));
      setTeamTasks(rows);
    } catch (error) {
      console.error("Ekip görevleri yüklenirken hata:", error);
      setTeamTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Düzenleme modunu aç/kapat
  const handleEditToggle = () => {
    if (isEditMode) {
      // Düzenleme modundan çıkarken, değerleri orijinal değerlere geri al
      setEditableName(selectedTeam.name || '');
      setEditableDescription(selectedTeam.description || '');
      setEditableColor(selectedTeam.color || '#1976D2');
    }
    setIsEditMode(!isEditMode);
  };

  // Yeni üye ekleme dialogunu aç
  const handleOpenAddMemberDialog = () => {
    setSelectedPersonnel(null);
    setSelectedRole('Görevli');
    setSelectedDuty('');
    setAddMemberDialogOpen(true);
  };

  // Yeni üye ekleme dialogunu kapat
  const handleCloseAddMemberDialog = () => {
    setAddMemberDialogOpen(false);
  };

  // Değişiklikleri kaydet — SQL (taskDataService)
  const handleSave = async () => {
    if (!companyId || !selectedTeam?.sqlId) return;
    
    setSaveLoading(true);
    try {
      const changed =
        editableName !== selectedTeam.name ||
        editableDescription !== selectedTeam.description ||
        editableColor !== selectedTeam.color;

      if (changed) {
        await taskDataService.updateTeamGroup({
          id: selectedTeam.sqlId,
          name: editableName,
          description: editableDescription,
          color: editableColor,
        });

        // Seçili ekibi yerelde güncelle
        selectedTeam.name = editableName;
        selectedTeam.description = editableDescription;
        selectedTeam.color = editableColor;

        setSnackbarMessage('Ekip bilgileri başarıyla güncellendi');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        onSaved?.();
      }
      
      setIsEditMode(false);
    } catch (error) {
      console.error("Ekip bilgileri güncellenirken hata:", error);
      setSnackbarMessage('Bilgiler güncellenirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  // Ekipten üye çıkar
  const handleRemoveMember = (memberId: string) => {
    setRemovingMemberId(memberId);
    setConfirmDeleteOpen(true);
  };

  // Üye çıkarma işlemini onayla — SQL (removingMemberId = personel uid = members map anahtarı)
  const confirmRemoveMember = async () => {
    if (!companyId || !selectedTeam?.sqlId || !removingMemberId) return;
    
    try {
      await taskDataService.removeTeamMember(selectedTeam.sqlId, { personnelFirebaseUid: removingMemberId });
      
      // Ekip üyelerini güncelle (UI'da göstermek için)
      if (selectedTeam && selectedTeam.members) {
        const { [removingMemberId]: _, ...updatedMembers } = selectedTeam.members;
        selectedTeam.members = updatedMembers;
      }
      
      setSnackbarMessage('Üye ekipten çıkarıldı');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      onSaved?.();
    } catch (error) {
      console.error("Üye çıkarılırken hata:", error);
      setSnackbarMessage('Üye çıkarılırken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setConfirmDeleteOpen(false);
      setRemovingMemberId(null);
    }
  };

  // Yeni üye ekle — SQL (selectedPersonnel = personel uid)
  const handleAddMember = async () => {
    if (!companyId || !selectedTeam?.sqlId || !selectedPersonnel) return;
    
    try {
      const selectedPersonnelData = personnel.find(p => p.id === selectedPersonnel);
      if (!selectedPersonnelData) {
        throw new Error('Seçilen personel bulunamadı');
      }
      
      const memberData = {
        name: selectedPersonnelData.name,
        memberRole: selectedRole,
        duty: selectedDuty,
      };
      
      await taskDataService.addTeamMember(selectedTeam.sqlId, {
        personnelFirebaseUid: selectedPersonnel,
        ...memberData,
      });
      
      // UI'ı güncelle (members map anahtarı = uid)
      if (selectedTeam) {
        if (!selectedTeam.members) selectedTeam.members = {};
        selectedTeam.members[selectedPersonnel] = { ...memberData, personnelFirebaseUid: selectedPersonnel };
      }
      
      setSnackbarMessage('Üye başarıyla eklendi');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setAddMemberDialogOpen(false);
      onSaved?.();
    } catch (error) {
      console.error("Üye eklenirken hata:", error);
      setSnackbarMessage('Üye eklenirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Üyeleri göster
  const renderMembers = () => {
    if (!selectedTeam || !selectedTeam.members) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          Bu ekibe henüz üye eklenmemiş.
        </Typography>
      );
    }
    
    const members = Object.entries(selectedTeam.members);
    const paginatedMembers = members.slice(
      membersPage * membersRowsPerPage,
      membersPage * membersRowsPerPage + membersRowsPerPage
    );
    
    return (
      <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ad Soyad</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Görev</TableCell>
                {isEditMode && <TableCell align="right">İşlemler</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMembers.map(([memberId, memberData]: [string, any]) => (
                <TableRow key={memberId} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 30, height: 30, mr: 1, bgcolor: selectedTeam.color }}>
                        {memberData.name ? memberData.name[0] : '?'}
                      </Avatar>
                      <Typography variant="body2">{memberData.name || 'İsimsiz Üye'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={memberData.memberRole || 'Belirsiz'} 
                      size="small"
                      color={
                        memberData.memberRole === 'Lider' ? 'primary' :
                        memberData.memberRole === 'Görevli' ? 'info' : 'default'
                      }
                      sx={{ fontWeight: 'medium' }}
                    />
                  </TableCell>
                  <TableCell>{memberData.duty || '-'}</TableCell>
                  {isEditMode && (
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleRemoveMember(memberId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={members.length}
          page={membersPage}
          onPageChange={(event, newPage) => setMembersPage(newPage)}
          rowsPerPage={membersRowsPerPage}
          onRowsPerPageChange={(event) => {
            setMembersRowsPerPage(parseInt(event.target.value, 10));
            setMembersPage(0);
          }}
          labelRowsPerPage="Sayfa başına üye:"
          labelDisplayedRows={({ from, to, count }) => `${count} üyeden ${from}-${to} arası`}
        />
      </>
    );
  };

  // Görevleri göster
  const renderTasks = () => {
    if (loadingTasks) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={30} />
        </Box>
      );
    }
    
    if (teamTasks.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          Bu ekibe henüz görev atanmamış.
        </Typography>
      );
    }
    
    const paginatedTasks = teamTasks.slice(
      tasksPage * tasksRowsPerPage,
      tasksPage * tasksRowsPerPage + tasksRowsPerPage
    );
    
    return (
      <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Görev Adı</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Atanma Tarihi</TableCell>
                <TableCell>Son Tarih</TableCell>
                <TableCell>İlerleme</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTasks.map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell>
                    <Typography variant="body2">{task.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.description && task.description.length > 40
                        ? `${task.description.substring(0, 40)}...`
                        : task.description || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusText(task.status)} 
                      size="small"
                      sx={{ 
                        bgcolor: `${getStatusColor(task.status)}20`,
                        color: getStatusColor(task.status),
                        fontWeight: 'medium'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {task.createdAt ? new Date(task.createdAt).toLocaleDateString('tr-TR') : '-'}
                  </TableCell>
                  <TableCell>
                    {task.dueDate ? new Date(task.dueDate).toLocaleDateString('tr-TR') : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: 60, 
                          height: 6, 
                          bgcolor: 'grey.200', 
                          borderRadius: 3,
                          mr: 1,
                          position: 'relative'
                        }}
                      >
                        <Box 
                          sx={{ 
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: `${task.progress || 0}%`,
                            bgcolor: task.progress === 100 ? 'success.main' : 'primary.main',
                            borderRadius: 3 
                          }}
                        />
                      </Box>
                      <Typography variant="caption">{task.progress || 0}%</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={teamTasks.length}
          page={tasksPage}
          onPageChange={(event, newPage) => setTasksPage(newPage)}
          rowsPerPage={tasksRowsPerPage}
          onRowsPerPageChange={(event) => {
            setTasksRowsPerPage(parseInt(event.target.value, 10));
            setTasksPage(0);
          }}
          labelRowsPerPage="Sayfa başına görev:"
          labelDisplayedRows={({ from, to, count }) => `${count} görevden ${from}-${to} arası`}
        />
      </>
    );
  };

  return (
    <StyledModal
      open={open}
      onClose={() => {
        setIsEditMode(false);
        onClose();
      }}
      aria-labelledby="ekip-detay-modal"
    >
      <ModalContent>
        {selectedTeam && (
          <>
            <ModalHeader>
              <Typography variant="h5" fontWeight="bold">
                Ekip Ayrıntıları
              </Typography>
              <Box>
                {isEditMode ? (
                  <>
                    <Button 
                      onClick={handleEditToggle} 
                      startIcon={<CancelIcon />} 
                      sx={{ mr: 1 }}
                      color="inherit"
                      disabled={saveLoading}
                    >
                      İptal
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleSave} 
                      startIcon={saveLoading ? <CircularProgress size={18} color="inherit"/> : <SaveIcon />} 
                      disabled={saveLoading}
                    >
                      Kaydet
                    </Button>
                  </>
                ) : (
                  <Button variant="outlined" onClick={handleEditToggle} startIcon={<EditIcon />}>
                    Düzenle
                  </Button>
                )}
                <IconButton 
                  onClick={() => {
                    setIsEditMode(false);
                    onClose();
                  }} 
                  sx={{ ml: 1 }}
                  disabled={saveLoading}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </ModalHeader>
            
            <Grid container spacing={3}>
              {/* Sol Panel - Ekip bilgileri ve performans */}
              <Grid item xs={12} md={3}>
                <Stack spacing={2.5}>
                  {/* Ekip Bilgileri */}
                  <ModalSection sx={{ textAlign: 'center' }}>
                    <Box 
                      sx={{ 
                        p: 0.5, 
                        height: 8, 
                        bgcolor: isEditMode ? editableColor : (selectedTeam.color || '#1976D2'), 
                        borderRadius: '4px 4px 0 0',
                        mb: 2
                      }} 
                    />
                    <Avatar 
                      sx={{ 
                        width: 100, 
                        height: 100, 
                        bgcolor: isEditMode ? editableColor : (selectedTeam.color || '#1976D2'),
                        fontSize: 40,
                        mb: 1.5,
                        mx: 'auto'
                      }}
                    >
                      <GroupsIcon fontSize="large" />
                    </Avatar>
                    
                    {isEditMode ? (
                      <>
                        <TextField
                          fullWidth
                          label="Ekip Adı"
                          value={editableName}
                          onChange={(e) => setEditableName(e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          label="Ekip Rengi"
                          value={editableColor}
                          onChange={(e) => setEditableColor(e.target.value)}
                          variant="outlined"
                          size="small"
                          type="color"
                          sx={{ mb: 2 }}
                        />
                      </>
                    ) : (
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {selectedTeam.name}
                      </Typography>
                    )}
                    
                    <Typography variant="caption" color="text.secondary" display="block">
                      Oluşturulma: {selectedTeam.createdAt ? new Date(selectedTeam.createdAt).toLocaleDateString('tr-TR') : '-'}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary" display="block">
                      Üye Sayısı: {selectedTeam.members ? Object.keys(selectedTeam.members).length : 0}
                    </Typography>
                  </ModalSection>

                  {/* Ekip Başarımları */}
                  <ModalSection>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                      <StarIcon fontSize="small" sx={{ mr: 1 }} />
                      Ekip Başarımları
                    </Typography>
                    
                    <List dense disablePadding>
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <AssignmentIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={<Typography variant="body2" color="text.secondary">Tamamlanan Görevler</Typography>} 
                          secondary={<Typography variant="body1" fontWeight={500}>0</Typography>} 
                        />
                      </ListItem>
                      
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PieChartIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={<Typography variant="body2" color="text.secondary">Ortalama Başarı</Typography>} 
                          secondary={<Typography variant="body1" fontWeight={500}>%0</Typography>} 
                        />
                      </ListItem>
                      
                      <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <WorkHistoryIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={<Typography variant="body2" color="text.secondary">Son Aktivite</Typography>} 
                          secondary={<Typography variant="body1" fontWeight={500}>-</Typography>} 
                        />
                      </ListItem>
                    </List>
                  </ModalSection>

                  {/* Yeni Üye Ekleme */}
                  {isEditMode && (
                    <ModalSection>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                        Üye İşlemleri
                      </Typography>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        startIcon={<PersonAddIcon />}
                        sx={{ borderRadius: 1.5, textTransform:'none', py:1 }}
                        onClick={handleOpenAddMemberDialog}
                      >
                        Yeni Üye Ekle
                      </Button>
                    </ModalSection>
                  )}
                </Stack>
              </Grid>

              {/* Orta ve Sağ Panel - Ekip açıklaması, üyeler ve görevler */}
              <Grid item xs={12} md={9}>
                <Stack spacing={2.5}>
                  {/* Ekip Açıklaması */}
                  <ModalSection>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1 }} />
                      Ekip Açıklaması
                    </Typography>
                    
                    {isEditMode ? (
                      <TextField
                        fullWidth
                        label="Açıklama"
                        value={editableDescription}
                        onChange={(e) => setEditableDescription(e.target.value)}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                      />
                    ) : (
                      <Typography variant="body1">
                        {selectedTeam.description || 'Bu ekip için açıklama bulunmamaktadır.'}
                      </Typography>
                    )}
                  </ModalSection>

                  {/* Ekip Üyeleri */}
                  <ModalSection>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                      <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                      Ekip Üyeleri
                    </Typography>
                    
                    {renderMembers()}
                  </ModalSection>

                  {/* Ekip Görevleri */}
                  <ModalSection>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                      <AssignmentIndIcon fontSize="small" sx={{ mr: 1 }} />
                      Ekip Görevleri
                    </Typography>
                    
                    {renderTasks()}
                  </ModalSection>
                </Stack>
              </Grid>
            </Grid>
          </>
        )}

        {/* Üye çıkarma onay dialogu */}
        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
        >
          <DialogTitle>Üyeyi Çıkar</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Bu üyeyi ekipten çıkarmak istediğinize emin misiniz?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} color="inherit">
              İptal
            </Button>
            <Button onClick={confirmRemoveMember} color="error" variant="contained">
              Çıkar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Yeni üye ekleme dialogu */}
        <Dialog
          open={addMemberDialogOpen}
          onClose={handleCloseAddMemberDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Ekibe Yeni Üye Ekle</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="personnel-select-label">Personel</InputLabel>
                <Select
                  labelId="personnel-select-label"
                  value={selectedPersonnel || ''}
                  onChange={(e) => setSelectedPersonnel(e.target.value)}
                  label="Personel"
                >
                  <MenuItem value="" disabled>
                    <em>Personel seçin</em>
                  </MenuItem>
                  {personnel
                    .filter(p => {
                      // Zaten ekipte olan personeli filtrele
                      return !selectedTeam?.members || 
                        !selectedTeam.members[p.id];
                    })
                    .map(p => (
                      <MenuItem key={p.id} value={p.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
                            {p.name ? p.name[0] : '?'}
                          </Avatar>
                          {p.name}
                        </Box>
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="role-select-label">Ekipteki Rolü</InputLabel>
                <Select
                  labelId="role-select-label"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  label="Ekipteki Rolü"
                >
                  <MenuItem value="Lider">Lider</MenuItem>
                  <MenuItem value="Görevli">Görevli</MenuItem>
                  <MenuItem value="İzleyici">İzleyici</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Ekipteki Görevi"
                value={selectedDuty}
                onChange={(e) => setSelectedDuty(e.target.value)}
                placeholder="Ör: Tasarımcı, Geliştirici, Analist..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddMemberDialog} color="inherit">
              İptal
            </Button>
            <Button 
              onClick={handleAddMember} 
              variant="contained" 
              color="primary"
              disabled={!selectedPersonnel}
            >
              Ekle
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bildirim Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity={snackbarSeverity} 
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </ModalContent>
    </StyledModal>
  );
};

export default TeamDetailsModal; 